import { Report } from '@/domain/types/collections';
import uploadFile from '@/domain/use-cases/upload-file';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { z } from 'zod';
import Button from '../button';
import { InputForm } from '../inputs/input-form';
import { SelectForm } from '../inputs/select-form';
import PdfUpload from '../pdf-upload';

const reportSchema = z.object({
  title: z.string().min(1, 'El título es requerido'),
  titleEN: z.string().min(1, 'El título en inglés es requerido'),
  category: z.enum(
    ['blasting', 'meeting', 'brigades', 'safety', 'incidents', 'maintenance'],
    {
      required_error: 'La categoría es requerida',
    },
  ),
  documentUrl: z.string().optional(),
  documentUrlEN: z.string().optional(),
});

interface FormAddReportProps {
  onSave: (data: Report) => void;
  document?: Report;
}

const FormAddReport = ({ onSave, document }: FormAddReportProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pdf, setPdf] = useState<File | null>(null);
  const [pdfEN, setPdfEN] = useState<File | null>(null);

  const methods = useForm<Report>({
    resolver: zodResolver(reportSchema),
    defaultValues: document,
  });

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      let documentUrl = data.documentUrl;
      let documentUrlEN = data.documentUrlEN;

      if (pdf) {
        documentUrl = await uploadFile({ file: pdf, path: 'pdfs' });
      }

      if (pdfEN) {
        documentUrlEN = await uploadFile({ file: pdfEN, path: 'pdfs' });
      }

      // Completar los campos faltantes con valores por defecto
      const reportData: Report = {
        ...document,
        title: data.title,
        titleEN: data.titleEN,
        description: '', // Campo obligatorio pero vacío
        descriptionEN: '', // Campo obligatorio pero vacío
        category: data.category,
        documentUrl: documentUrl || null,
        documentUrlEN: documentUrlEN || null,
      };

      await onSave(reportData);
    } catch (error) {
      console.error('Error uploading documento:', error);
      toast.error('Error al subir el documento');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-4 flex-1 bg-white rounded-lg shadow-md p-12">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <label className="block text-sm font-medium text-green-900 mb-2">
                  Categoría del reporte
                </label>
                <SelectForm
                  name="category"
                  placeholder="Selecciona una categoría"
                  options={[
                    {
                      value: 'blasting',
                      label: 'Blasting Schedule / Cronograma de Voladura',
                    },
                    {
                      value: 'meeting',
                      label:
                        'Mine Daily Meeting Safety Report / Reporte de Seguridad de Reunión Diaria de Mina',
                    },
                    {
                      value: 'brigades',
                      label:
                        'Brigades Training Report / Reporte de Entrenamiento de Brigadas',
                    },
                    {
                      value: 'safety',
                      label:
                        'Safety Training Reports / Reportes de Entrenamiento de Seguridad',
                    },
                    {
                      value: 'incidents',
                      label:
                        'Shift Incidents Command System / Sistema de Comando de Incidentes de Turno',
                    },
                    {
                      value: 'maintenance',
                      label: 'Maintenance Report / Reporte de Mantenimiento',
                    },
                  ]}
                />
              </div>
              <InputForm name="title" placeholder="Título" />
              <InputForm name="titleEN" placeholder="Título en inglés" />
              <div className="flex flex-col gap-2">
                <span className="text-sm text-gray-500">
                  Documento en español (opcional)
                </span>
                <PdfUpload
                  onFileChange={setPdf}
                  pdf={document?.documentUrl}
                  id="pdf-upload"
                />
                <span className="text-sm text-gray-500">
                  Documento en inglés (opcional)
                </span>
                <PdfUpload
                  onFileChange={setPdfEN}
                  pdf={document?.documentUrlEN}
                  id="pdf-upload-en"
                />
              </div>
              <div className="flex justify-center mt-3">
                <Button
                  label="Guardar"
                  type="submit"
                  isLoading={isLoading}
                  onClick={() => methods.handleSubmit(onSubmit)}
                  icon={<FiSave />}
                  className="w-[200px]"
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default FormAddReport;
